import { useMemo, useEffect, useRef, useCallback, forwardRef } from 'react';
import styled from 'styled-components';
import { Tooltip } from '@mui/material';
import Chip from '../../Chip';
import { Flex } from '../../Flex';
import type { AgentContext } from '../../../types';
import { pluralizeString } from '../../../utils/common';
import { fontSize, space } from '../../../style/utils';

const Overlay = styled(Flex)`
  position: absolute;
  top: 10px;
  left: 14px;
  height: 21px;
  align-items: center;
  gap: ${space('xs')};
  z-index: 1;
`;

const StyledChip = styled(Chip)`
  font-size: ${fontSize('sm')};
  height: 21px;
  color: ${({ theme }) => theme.palette.content.primary};
`;

type Props = {
  context?: AgentContext;
  onWidthChange: (width: number) => void;
  onClear?: () => void;
};

const ContextChips = forwardRef<HTMLDivElement, Props>(
  ({ context, onWidthChange, onClear }, ref) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
        containerRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          Object.assign(ref, { current: node });
        }
      },
      [ref],
    );

    const chips = useMemo(() => {
      if (!context) return [];
      const result: {
        label: string;
        ids: string[];
        tooltipLabels: string[];
      }[] = [];
      const labelsForIds = (ids: string[]): string[] =>
        context.displayLabels?.length === ids.length
          ? context.displayLabels
          : ids;
      if (context.testIds?.length) {
        const count = context.testIds.length;
        result.push({
          label: pluralizeString(count, 'test', 'tests'),
          ids: context.testIds,
          tooltipLabels: labelsForIds(context.testIds),
        });
      }
      if (context.assetIds?.length) {
        const count = context.assetIds.length;
        result.push({
          label: pluralizeString(count, 'asset', 'assets'),
          ids: context.assetIds,
          tooltipLabels: labelsForIds(context.assetIds),
        });
      }
      if (context.incidentIds?.length) {
        const count = context.incidentIds.length;
        result.push({
          label: pluralizeString(count, 'incident', 'incidents'),
          ids: context.incidentIds,
          tooltipLabels: labelsForIds(context.incidentIds),
        });
      }
      return result;
    }, [context]);

    const hasChips = chips.length > 0;

    const updateWidth = useCallback(() => {
      if (containerRef.current) {
        onWidthChange(containerRef.current.offsetWidth + 6);
      } else {
        onWidthChange(0);
      }
    }, [onWidthChange]);

    useEffect(() => {
      const el = containerRef.current;
      if (!el || !hasChips) {
        onWidthChange(0);
        return undefined;
      }

      const observer = new ResizeObserver(updateWidth);
      observer.observe(el);
      updateWidth();

      return () => observer.disconnect();
    }, [hasChips, updateWidth, onWidthChange]);

    if (!hasChips) return null;

    return (
      <Overlay ref={setRefs}>
        {chips.map((chip) => (
          <Tooltip
            key={chip.label}
            title={
              <div>
                {chip.tooltipLabels.map((value) => (
                  <div key={value}>{value}</div>
                ))}
              </div>
            }
          >
            <span>
              <StyledChip label={chip.label} onDelete={onClear} />
            </span>
          </Tooltip>
        ))}
      </Overlay>
    );
  },
);

export default ContextChips;
